/**
 * East Africa Time (EAT) utilities.
 *
 * The backend stores/treats scheduled class/session dates & times as
 * East Africa Time (UTC+3). Browsers parse `"2026-04-18T05:30"` in the
 * *device* local timezone, which breaks countdowns whenever the user's
 * device is not in EAT.
 *
 * These helpers anchor everything to EAT regardless of device TZ,
 * removing ambiguity.
 */

export const EAT_OFFSET = '+03:00';

/**
 * Build a Date from the user-facing scheduled date+time and EAT.
 * Returns null if either piece is missing.
 *
 * @param {string} dateStr e.g. "2026-04-18"
 * @param {string|undefined} timeStr e.g. "05:30" or "05:30:00"
 */
export function parseEAT(dateStr, timeStr) {
  if (!dateStr) return null;
  let t = (timeStr || '00:00').trim();
  // Normalize to HH:MM:SS (Safari is picky)
  if (/^\d{2}:\d{2}$/.test(t)) t = `${t}:00`;
  // If `dateStr` is already an ISO with timezone (e.g. "2026-04-18T05:30:00Z"
  // or "2026-04-18T05:30:00+03:00"), trust it as-is.
  if (/T.*[+\-Z]/.test(dateStr)) {
    const d = new Date(dateStr);
    return isFinite(d.getTime()) ? d : null;
  }
  // If date is just "YYYY-MM-DD" we anchor in EAT.
  const iso = `${dateStr}T${t}${EAT_OFFSET}`;
  const d = new Date(iso);
  return isFinite(d.getTime()) ? d : null;
}

/**
 * Milliseconds between a scheduled (date, time) in EAT and `now`.
 * Positive = future, negative = past.
 */
export function msUntilEAT(dateStr, timeStr, now = new Date()) {
  const d = parseEAT(dateStr, timeStr);
  if (!d) return null;
  return d.getTime() - now.getTime();
}

/**
 * Return { hours, minutes, seconds, diff } from a positive millisecond diff.
 */
export function breakdownMs(diff) {
  if (diff == null || diff < 0) diff = 0;
  const hours = Math.floor(diff / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { hours, minutes, seconds, diff };
}

/**
 * "Wall clock" formatter — renders a UTC instant as HH:MM EAT regardless
 * of device TZ. Useful for chat timestamps, recording timestamps, etc.
 */
export function formatEATTime(isoOrDate) {
  const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  if (!isFinite(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: '2-digit', minute: '2-digit', hour12: false,
      timeZone: 'Africa/Dar_es_Salaam',
    }).format(d);
  } catch {
    // Manually offset by +03:00
    const ms = d.getTime() + 3 * 3600 * 1000;
    const eatDate = new Date(ms);
    return `${String(eatDate.getUTCHours()).padStart(2, '0')}:${String(eatDate.getUTCMinutes()).padStart(2, '0')}`;
  }
}
